import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { formatHref } from '@/utils/formatHref';

export const usePreviousRoute = () => {
	const router = useNextRouter();
	const [previousPath, setPreviousPath] = useState<{
		pathname: string;
		query: any;
	} | null>(null);
	const didCheckReferrer = useRef(false);

	useEffect(() => {
		// 1) En el primer render, intenta deducir la ruta previa desde el referrer
		//    SOLO si es del mismo origen (mismo sitio).
		if (!didCheckReferrer.current) {
			didCheckReferrer.current = true;

			if (typeof window !== 'undefined' && document?.referrer) {
				try {
					const ref = new URL(document.referrer);
					const { origin } = window.location;

					if (ref.origin === origin) {
						// Conservar pathname + query + hash (si aplica)
						const prev = ref.pathname + (ref.search || '') + (ref.hash || '');
						const href = formatHref(prev);
						setPreviousPath(href || { pathname: '/', query: {} });
					}
				} catch {
					// Ignorar errores de parseo del referrer
				}
			}
		}

		// 2) En navegaciones internas, guarda la ruta actual como "previa"
		const handleRouteChangeStart = (_url: string) => {
			setPreviousPath({ pathname: router.asPath, query: router.query });
			const href = formatHref(router.asPath);
			setPreviousPath(href || { pathname: '/', query: {} });
		};

		// Opcional: también cubrir cambios de hash internos
		const handleHashChangeStart = (_url: string) => {
			setPreviousPath({ pathname: router.asPath, query: router.query });
			const href = formatHref(router.asPath);
			setPreviousPath(href || { pathname: '/', query: {} });
		};

		router.events.on('routeChangeStart', handleRouteChangeStart);
		router.events.on('hashChangeStart', handleHashChangeStart);

		return () => {
			router.events.off('routeChangeStart', handleRouteChangeStart);
			router.events.off('hashChangeStart', handleHashChangeStart);
		};
	}, [router]);

	return previousPath;
};

const PreviousRouteContext = createContext<{ pathname: string; query: any } | null>(null);

export const PreviousRouteProvider = ({ children }: { children: React.ReactNode }) => {
	const previousRoute = usePreviousRoute();
	return (
		<PreviousRouteContext.Provider value={previousRoute}>{children}</PreviousRouteContext.Provider>
	);
};

export const usePreviousRouteContext = () => {
	const context = useContext(PreviousRouteContext);

	if (context === undefined) {
		throw new Error('usePreviousRouteContext must be used within a PreviousRouteProvider');
	}
	return context;
};
